/**
 * Product Modal Enhancement Module
 * Adds swipe functionality and thumbnail navigation to Dawn's product modal
 */

export default class ProductModalEnhancement {
  constructor() {
    this.modal = null;
    this.content = null;
    this.slides = [];
    this.currentIndex = 0;
    this.thumbnailContainer = null;
    this.isEnhanced = false;
    this.originalHide = null;
    
    this.init();
    this.addPreventFlickerStyles();
  }

  addPreventFlickerStyles() {
    // Add CSS to hide modal until enhanced
    if (!document.querySelector('#modal-flicker-prevent')) {
      const style = document.createElement('style');
      style.id = 'modal-flicker-prevent';
      style.textContent = `
        product-modal:not(.enhanced) {
          visibility: hidden !important;
        }
        product-modal.enhanced {
          visibility: visible !important;
        }
      `;
      document.head.appendChild(style);
    }
  }

  init() {
    // Intercept modal opening
    document.addEventListener('click', (e) => {
      const mediaButton = e.target.closest('.product__media-item');
      if (mediaButton) {
        // Enhance immediately when clicked
        setTimeout(() => this.enhanceModal(), 10);
      }
    });
  }

  enhanceModal() {
    this.modal = document.querySelector('product-modal');
    if (!this.modal) return;

    this.content = this.modal.querySelector('.product-media-modal__content');
    if (!this.content) return;

    // Check if already enhanced - if so, just reset state
    if (this.isEnhanced) {
      this.resetModalState();
      return;
    }

    // IMMEDIATELY lock scroll position before Dawn can change it
    this.content.scrollLeft = 0;
    this.content.style.scrollBehavior = 'auto';

    // Mark as enhanced - this will make it visible via CSS
    this.isEnhanced = true;
    this.modal.classList.add('enhanced');

    // Override the ModalDialog's hide method
    this.originalHide = this.modal.hide.bind(this.modal);
    this.modal.hide = () => {
      // Don't hide - we'll control this manually
    };

    // Override showActiveMedia to prevent auto-scrolling
    const originalShowActiveMedia = this.modal.showActiveMedia;
    this.modal.showActiveMedia = () => {
      // Call original to set up media properly
      if (originalShowActiveMedia) {
        originalShowActiveMedia.call(this.modal);
      }
      // But immediately reset scroll to first image
      if (this.content) {
        this.content.scrollLeft = 0;
      }
    };

    // Get all media slides
    this.slides = Array.from(this.content.children);
    if (this.slides.length <= 1) return;

    // Enable horizontal scroll
    this.enableSwipe();

    // Force scroll to first slide AGAIN
    setTimeout(() => {
      if (this.content) {
        this.content.scrollLeft = 0;
      }
    }, 0);

    // Wait a bit for images to load, then add thumbnails
    setTimeout(() => {
      this.addThumbnails();
    }, 300);

    // Add navigation arrows
    this.addArrows();

    // Track active slide
    this.trackActiveSlide();
    
    // Setup close button
    this.setupCloseButton();
  }

  setupCloseButton() {
    const toggleButton = this.modal.querySelector('.product-media-modal__toggle');
    
    if (toggleButton) {
      // Remove existing listeners by cloning
      const newToggle = toggleButton.cloneNode(true);
      toggleButton.parentNode.replaceChild(newToggle, toggleButton);
      
      newToggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.closeModal();
      });
    }
  }

  resetModalState() {
    // Reset to first slide immediately
    this.currentIndex = 0;
    if (this.content) {
      this.content.style.scrollBehavior = 'auto';
      this.content.scrollLeft = 0;
      
      // Re-enable smooth scrolling after reset
      setTimeout(() => {
        if (this.content) {
          this.content.style.scrollBehavior = 'smooth';
        }
      }, 50);
    }
    this.updateActiveSlide(0);
  }

  closeModal() {
    if (this.modal && this.originalHide) {
      // Restore original hide method
      this.modal.hide = this.originalHide;
      // Now call it
      this.modal.hide();
      // Reset state
      this.isEnhanced = false;
      this.modal.classList.remove('enhanced');
    }
  }

  enableSwipe() {
    this.content.style.display = 'flex';
    this.content.style.overflowX = 'scroll';
    this.content.style.scrollSnapType = 'x mandatory';
    this.content.style.WebkitOverflowScrolling = 'touch';
    this.content.style.scrollbarWidth = 'none';
    this.content.style.msOverflowStyle = 'none';

    if (!document.querySelector('#modal-scrollbar-style')) {
      const style = document.createElement('style');
      style.id = 'modal-scrollbar-style';
      style.textContent = '.product-media-modal.enhanced .product-media-modal__content::-webkit-scrollbar { display: none; }';
      document.head.appendChild(style);
    }

    this.slides.forEach((slide, index) => {
      slide.style.flex = '0 0 100%';
      slide.style.width = '100%';
      slide.style.scrollSnapAlign = 'start';
      slide.style.scrollSnapStop = 'always';
      slide.style.display = 'flex';
      slide.style.alignItems = 'center';
      slide.style.justifyContent = 'center';
      
      if (index === 0) {
        slide.classList.add('active');
      } else {
        slide.classList.remove('active');
      }
    });

    // Set scroll behavior to smooth AFTER initial positioning
    setTimeout(() => {
      this.content.style.scrollBehavior = 'smooth';
    }, 100);
  }

  addThumbnails() {
    const dialog = this.modal.querySelector('.product-media-modal__dialog');
    if (!dialog) return;

    const existingThumbnails = dialog.querySelector('.product-modal-thumbnails');
    if (existingThumbnails) {
      existingThumbnails.remove();
    }

    const galleryThumbnails = document.querySelectorAll('.product__media-item img, [data-media-id] img');
    
    this.thumbnailContainer = document.createElement('div');
    this.thumbnailContainer.className = 'product-modal-thumbnails';
    
    if (galleryThumbnails.length > 0) {
      galleryThumbnails.forEach((galleryImg, index) => {
        if (index >= this.slides.length) return;
        
        const thumbnail = document.createElement('button');
        thumbnail.className = 'product-modal-thumbnail';
        thumbnail.type = 'button';
        thumbnail.setAttribute('aria-label', 'Go to slide ' + (index + 1));
        
        const thumbImg = document.createElement('img');
        let srcUrl = galleryImg.src || galleryImg.dataset.src || galleryImg.getAttribute('src');
        
        if (srcUrl) {
          if (srcUrl.includes('cdn.shopify.com')) {
            srcUrl = srcUrl.split('?')[0];
            if (!srcUrl.includes('_120x')) {
              const parts = srcUrl.split('.');
              const ext = parts.pop();
              srcUrl = parts.join('.') + '_120x.' + ext;
            }
          }
          
          thumbImg.src = srcUrl;
          thumbImg.alt = galleryImg.alt || '';
          thumbImg.style.width = '100%';
          thumbImg.style.height = '100%';
          thumbImg.style.objectFit = 'cover';
          thumbImg.onerror = function() {
            this.src = galleryImg.src;
          };
          thumbnail.appendChild(thumbImg);
        }

        if (index === 0) {
          thumbnail.classList.add('active');
        }

        thumbnail.addEventListener('click', (e) => {
          e.stopPropagation();
          e.preventDefault();
          this.goToSlide(index);
        });

        this.thumbnailContainer.appendChild(thumbnail);
      });
    }

    dialog.appendChild(this.thumbnailContainer);
  }

  addArrows() {
    const dialog = this.modal.querySelector('.product-media-modal__dialog');
    if (!dialog) return;

    const existingPrev = dialog.querySelector('.product-modal-arrow--prev');
    const existingNext = dialog.querySelector('.product-modal-arrow--next');
    if (existingPrev) existingPrev.remove();
    if (existingNext) existingNext.remove();

    const prevButton = document.createElement('button');
    prevButton.className = 'product-modal-arrow product-modal-arrow--prev';
    prevButton.type = 'button';
    prevButton.setAttribute('aria-label', 'Previous slide');
    prevButton.innerHTML = '<svg viewBox="0 0 10 6" aria-hidden="true"><path d="M9 5 5 1 1 5" stroke="currentColor" fill="none" stroke-width="2"/></svg>';
    
    prevButton.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.prevSlide();
    });

    const nextButton = document.createElement('button');
    nextButton.className = 'product-modal-arrow product-modal-arrow--next';
    nextButton.type = 'button';
    nextButton.setAttribute('aria-label', 'Next slide');
    nextButton.innerHTML = '<svg viewBox="0 0 10 6" aria-hidden="true"><path d="M9 5 5 1 1 5" stroke="currentColor" fill="none" stroke-width="2"/></svg>';
    
    nextButton.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.nextSlide();
    });

    dialog.appendChild(prevButton);
    dialog.appendChild(nextButton);
  }

  trackActiveSlide() {
    let scrollTimeout;
    this.content.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const scrollLeft = this.content.scrollLeft;
        const slideWidth = this.content.offsetWidth;
        const newIndex = Math.round(scrollLeft / slideWidth);
        
        if (newIndex !== this.currentIndex) {
          this.updateActiveSlide(newIndex);
        }
      }, 100);
    });
  }

  updateActiveSlide(index) {
    this.currentIndex = index;

    this.slides.forEach((slide, i) => {
      slide.classList.toggle('active', i === index);
    });

    if (this.thumbnailContainer) {
      const thumbnails = this.thumbnailContainer.querySelectorAll('.product-modal-thumbnail');
      thumbnails.forEach((thumb, i) => {
        thumb.classList.toggle('active', i === index);
      });
    }
  }

  goToSlide(index) {
    const slideWidth = this.content.offsetWidth;
    this.content.scrollTo({
      left: slideWidth * index,
      behavior: 'smooth'
    });
    this.updateActiveSlide(index);
  }

  prevSlide() {
    const newIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.slides.length - 1;
    this.goToSlide(newIndex);
  }

  nextSlide() {
    const newIndex = this.currentIndex < this.slides.length - 1 ? this.currentIndex + 1 : 0;
    this.goToSlide(newIndex);
  }
}